<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useContentStore } from '@/stores/content.store'
import { useFrontpageStore } from '@/stores/frontpage.store'
import { useHeaderNavigationStore } from '@/stores/headerNavigation.store'
import { useHead } from '@unhead/vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { ensureCookieConsentReady, showCookieConsentIfNeeded } from '@/bootstrap/deferredClientModules'
import { useLayout } from '@/composables/useLayout'
import { useRouteSeoContent } from '@/composables/useRouteSeoContent'
import { getSeoDefaults, getSeoSiteName, getSeoSiteOrigin } from '@/config/seo'
import { useUIStore } from '@/stores/ui.store'
import { buildSeoHead } from '@/utils/seo'

const store = useContentStore()
const frontpageStore = useFrontpageStore()
const headerNavigationStore = useHeaderNavigationStore()
const { gyms, isLoading, pages, posts, trainers } = storeToRefs(store)
const { frontpage } = storeToRefs(frontpageStore)
const hasPrerenderedApp = import.meta.env.SSR || (
  typeof document !== 'undefined'
  && document.getElementById('app')?.dataset.serverRendered === 'true'
)
const isHeaderReady = ref(hasPrerenderedApp)
const isCookieUiReady = ref(false)
const isRouteDomReady = ref(hasPrerenderedApp)

const ui = useUIStore()
const router = useRouter()
const route = useRoute()
const { locale, t } = useI18n()
const { layoutComponent } = useLayout()
const { attachments, currentRouteContent } = useRouteSeoContent()
const BACKEND_RETRY_INTERVAL_MS = 30_000
let backendRetryTimer: ReturnType<typeof setInterval> | null = null
let contentRevalidationTimer: ReturnType<typeof setTimeout> | null = null
let isBackendRetrying = false
let removePagesRouteGuard: (() => void) | null = null

const normalizedRouteName = computed(() => {
  if (typeof route.name !== 'string') return ''
  return route.name.replace(/-(en|no)$/, '')
})

function animationFrame() {
  if (typeof window === 'undefined') return Promise.resolve()
  return new Promise((resolve) => window.requestAnimationFrame(resolve))
}

async function waitForRouteDomPaint() {
  await nextTick()
  await animationFrame()
  await animationFrame()
}

watch(
  normalizedRouteName,
  (name) => {
    if (name === 'home') {
      void frontpageStore.ensure()
    }
  },
  { immediate: true }
)

watch(
  () => locale.value,
  async (nextLocale, previousLocale) => {
    if (!previousLocale || nextLocale === previousLocale) return

    await Promise.all([
      store.ensureContent({ force: true }),
      frontpageStore.ensure({ force: true }),
      headerNavigationStore.revalidate(),
    ])
  },
  { flush: 'sync' },
)

useHead(() => {
  const isNotFound = normalizedRouteName.value === '404'
  const isPreview = normalizedRouteName.value === 'preview'
  const head = buildSeoHead({
    routePath: route.path,
    locale: typeof locale.value === 'string' ? locale.value : String(locale.value ?? 'no'),
    siteName: getSeoSiteName(),
    siteOrigin: getSeoSiteOrigin(),
    content: currentRouteContent.value,
    attachments: attachments.value,
    locations: gyms.value,
    defaults: getSeoDefaults(
      isNotFound || isPreview
        ? {
            title: isPreview ? t('preview.seoTitle') : t('notFound.seoTitle'),
            description: isPreview ? t('preview.description') : t('notFound.description'),
          }
        : undefined,
    ),
  })

  if (!isNotFound && !isPreview) return head
  const meta = Array.isArray(head.meta) ? head.meta : []

  return {
    ...head,
    meta: [
      ...meta,
      {
        name: 'robots',
        content: 'noindex, nofollow',
      },
    ],
  }
})

function removeBuildTimeJsonLdFallback() {
  if (typeof document === 'undefined') return

  document.querySelectorAll<HTMLScriptElement>(
    'script[type="application/ld+json"][data-build-time-jsonld="true"]',
  ).forEach((script) => {
    script.remove()
  })
}

watch(
  () => [route.fullPath, currentRouteContent.value],
  async () => {
    await nextTick()
    removeBuildTimeJsonLdFallback()
  },
  { flush: 'post', immediate: true },
)

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && ui.isOffcanvasOpen) {
    ui.closeOffcanvas()
  }
}

async function retryBackendContent() {
  if (isBackendRetrying) return

  isBackendRetrying = true
  try {
    await Promise.all([
      store.ensureContent({ force: true }),
      normalizedRouteName.value === 'home'
        ? frontpageStore.ensure({ force: true })
        : Promise.resolve(),
    ])
  } finally {
    isBackendRetrying = false
  }
}

function stopBackendRetryTimer() {
  if (!backendRetryTimer) return

  clearInterval(backendRetryTimer)
  backendRetryTimer = null
}

function startBackendRetryTimer() {
  if (backendRetryTimer) return

  backendRetryTimer = setInterval(() => {
    void retryBackendContent()
  }, BACKEND_RETRY_INTERVAL_MS)
}

function scheduleContentRevalidation() {
  if (!hasPrerenderedApp || contentRevalidationTimer) return

  contentRevalidationTimer = setTimeout(() => {
    contentRevalidationTimer = null
    void Promise.all([
      headerNavigationStore.revalidate(),
      normalizedRouteName.value === 'home'
        ? frontpageStore.revalidate()
        : Promise.resolve(),
    ])
  }, 0)
}

onMounted(async () => {
  if (hasPrerenderedApp) {
    removePagesRouteGuard = router.beforeResolve(async (to, from) => {
      if (to.fullPath === from.fullPath || store.hasFullCoverage('pages')) return

      await store.ensureFullResource('pages')
    })
  }

  try {
    await Promise.all([
      hasPrerenderedApp ? Promise.resolve() : headerNavigationStore.revalidate(),
      normalizedRouteName.value === 'home' ? frontpageStore.ensure() : Promise.resolve(),
    ])
  } finally {
    isHeaderReady.value = true
    scheduleContentRevalidation()
    await ensureCookieConsentReady()
    isCookieUiReady.value = true
    await nextTick()
    await showCookieConsentIfNeeded()
  }

  window.addEventListener('keydown', handleKeydown)

  router.afterEach(() => {
    ui.closeOffcanvas()
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
  stopBackendRetryTimer()
  removePagesRouteGuard?.()
  removePagesRouteGuard = null

  if (contentRevalidationTimer) {
    clearTimeout(contentRevalidationTimer)
    contentRevalidationTimer = null
  }
})

import AppPreloader from '@/components/AppPreloader.vue'
import AppModal from '@/components/modal/AppModal.vue'
import AppHeader from './layout/partials/AppHeader.vue'
import AppFooter from './layout/partials/AppFooter.vue'
import CookieButton from './components/common/CookieButton.vue'
import Notice from './components/common/Notice.vue'

const hasRouteContent = computed(() => {
  const slug = typeof route.params.slug === 'string' ? route.params.slug : ''

  switch (normalizedRouteName.value) {
    case 'home': {
      const frontpageId = frontpage.value?.frontpage_id
      return typeof frontpageId === 'number' && pages.value.some((page) => page.id === frontpageId)
    }
    case 'page':
      return pages.value.some((page) => page.slug === slug)
    case 'gym':
      return gyms.value.some((gym) => gym.slug === slug)
    case 'trainer':
      return trainers.value.some((trainer) => trainer.slug === slug)
    case 'campaign':
    case 'post':
      return posts.value.some((post) => post.slug === slug)
    case '404':
    case 'preview':
      return true
    default:
      return pages.value.length > 0 || posts.value.length > 0 || gyms.value.length > 0
  }
})

const isWaitingForBackendContent = computed(() =>
  ui.isBackendUnavailable && !hasRouteContent.value,
)

const isPreloaderVisible = computed(() =>
  !isHeaderReady.value
  || isLoading.value
  || isWaitingForBackendContent.value
  || !isRouteDomReady.value,
)

watch(
  () => [route.fullPath, hasRouteContent.value],
  async ([fullPath, hasContent]) => {
    const expectedFullPath = String(fullPath)
    isRouteDomReady.value = false

    if (!hasContent) return

    await waitForRouteDomPaint()

    if (route.fullPath === expectedFullPath && hasRouteContent.value) {
      isRouteDomReady.value = true
    }
  },
  { flush: 'post', immediate: true },
)

watch(
  isWaitingForBackendContent,
  (shouldRetry) => {
    if (shouldRetry) {
      startBackendRetryTimer()
      return
    }

    stopBackendRetryTimer()
  },
  { immediate: true },
)
</script>

<template>
  <AppPreloader v-if="isPreloaderVisible" />
  <AppHeader v-if="isHeaderReady" />
  <Notice v-if="!isPreloaderVisible" />
  <component 
    :is="layoutComponent" 
  />
  <AppModal />
  <CookieButton v-if="isCookieUiReady" />
  <AppFooter />
</template>
